<script setup lang="ts">
// The headline above the panels: the two numbers the question turns on — how
// many site-visits there were, and what share of them treated. It is a caption,
// not a hero band: the split panel directly beneath states all five outcomes
// with their counts and shares, so repeating them here would say the same
// thing twice and push the charts below the fold.
import { computed } from "vue";

import type { MapReviewWasteView } from "@/type/api";

import StatCard from "../charts/StatCard.vue";
import { formatCount, wasteShare } from "./wasteTheme";

const props = defineProps<{
	view: MapReviewWasteView;
}>();

interface Tile {
	label: string;
	value: string;
	sub: string;
}

const tiles = computed<Tile[]>(() => {
	const totals = props.view.totals;
	return [
		{
			label: "site visits",
			value: formatCount(totals.visits),
			sub: "one Site, one local day",
		},
		{
			label: "treated share",
			value: `${wasteShare(totals.treated, totals.visits).toFixed(1)}%`,
			sub: "of site visits",
		},
	];
});
</script>

<template>
	<section class="waste-stats" aria-label="This season at a glance">
		<StatCard
			v-for="tile in tiles"
			:key="tile.label"
			class="stat"
			:label="tile.label"
			:value="tile.value"
			:sub="tile.sub"
		/>
	</section>
</template>

<style scoped>
/* Two tiles, one line, at the rail's width: the section spans the panel grid
   because it is the question's headline, not one cell of it. */
.waste-stats {
	grid-column: 1 / -1;
	display: flex;
	gap: 6px;
	min-width: 0;
}

.stat {
	flex: 1 1 0;
	/* A fixed floor wider than the rail would push the row past the pane; the
	   two tiles split whatever width there is. */
	min-width: 0;
	padding: 6px 9px;
}

/* Smaller and tighter than the shared card's default, because this row is a
   caption above the charts rather than the dashboard's headline. The type is
   reduced here, through the scope boundary, so the population question's cards
   keep the size they were designed at. */
.stat :deep(.stat-value) {
	font-size: 1rem;
}

.stat :deep(.stat-label) {
	font-size: 0.68rem;
}

.stat :deep(.stat-sub) {
	font-size: 0.62rem;
}
</style>
